import { useCallback } from "react";
import { SelectColumn } from "react-data-grid";

export function useHarvestEntryNavigation({
  gridRef,
  visibleRowsLength,
  isDesktop,
  showWeight,
  undo,
  redo,
  isPseudoFullscreen,
  exitPseudoFullscreen,
  fillCellPositionRef,
}) {
  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape" && isPseudoFullscreen) {
        e.preventDefault();
        exitPseudoFullscreen();
        return;
      }

      const isCmd = e.metaKey || e.ctrlKey;

      // Enter on a fill column in SELECT mode (no editor input focused): open editor.
      // We check e.target to distinguish SELECT mode (grid cell div) from EDIT mode
      // (editor <input> or <textarea>). In EDIT mode the editor's own onKeyDown
      // handles Enter; we must not intercept or we'd fight it.
      if (
        e.key === "Enter" &&
        !isCmd &&
        fillCellPositionRef?.current &&
        !(e.target instanceof Element &&
          ["INPUT", "TEXTAREA"].includes(e.target.tagName))
      ) {
        e.preventDefault();
        gridRef.current?.selectCell?.(fillCellPositionRef.current, {
          enableEditor: true,
          shouldFocusCell: true,
        });
        return;
      }

      if (!isCmd) return;

      if (e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      } else if (e.key.toLowerCase() === "y") {
        e.preventDefault();
        redo();
      }
    },
    [undo, redo, isPseudoFullscreen, exitPseudoFullscreen, fillCellPositionRef],
  );

  const selectCell = useCallback((rowIdx, idx, enableEditor = true) => {
    gridRef.current?.selectCell?.(
      { rowIdx, idx },
      { enableEditor, shouldFocusCell: true },
    );
  }, []);

  const move = useCallback(
    ({ rowIdx, idx }, key, lastColIdx) => {
      const lastRow = visibleRowsLength - 1;
      let r = rowIdx;
      let c = idx;

      if (key === "Enter" || key === "ArrowDown")
        r = Math.min(rowIdx + 1, lastRow);
      if (key === "ArrowUp") r = Math.max(rowIdx - 1, 0);
      if (key === "ArrowRight") c = Math.min(idx + 1, lastColIdx);
      if (key === "ArrowLeft") c = Math.max(idx - 1, 0);

      selectCell(r, c, true);
    },
    [selectCell, visibleRowsLength],
  );

  const onCellClick = useCallback(
    (args) => {
      if (args.column.key === SelectColumn.key) return;

      const editable =
        typeof args.column.editable === "function"
          ? args.column.editable(args.row)
          : !!args.column.editable;

      if (!editable) return;

      const isFillColumn =
        args.column.key === "pendingYield" ||
        args.column.key === "pendingTubWeight" ||
        args.column.key === "pendingPloughedNote" ||
        (showWeight && args.column.key === "pendingWeight");

      gridRef.current?.selectCell?.(
        { rowIdx: args.rowIdx, idx: args.column.idx },
        { enableEditor: isDesktop ? !isFillColumn : true, shouldFocusCell: true },
      );
    },
    [isDesktop, showWeight],
  );

  return {
    onKeyDown,
    selectCell,
    move,
    onCellClick,
  };
}
